import { Module } from 'vuex';
// import { createStore } from 'vuex';
import { getCountdownFromTimestamp } from '@manifoldxyz/manifold-dropsitetools-lib';
import { OAuthGrantType } from '@manifoldxyz/manifold-sdk';
import { fetchWithTimeout } from '@/api/apiHelpers';
import { Question, RedemptionResult, Token } from '@/api/models';
import { CodeOptionMap } from '@/api/models/codeOption';
import RedeemServerClient, { Campaign, CampaignWithShopifyProduct } from '@/api/redeem/redeem';
import ShopifyClient from '@/api/shopify/shopify';
import { CampaignShopifyProductDetails } from '@/api/shopify/types';
import { TicketActionString } from '@/mixins/WithTicketActions';

export enum CountdownType {
  LOADING = 'loading',
  TO_START = 'to-start',
  TO_END = 'to-end',
  EXPIRED = 'expired',
  ACTIVE = 'active',
  NONE = 'none'
}

export type State = {
  mmid?: string;
  campaignId?: number;
  productId?: number;
  RedeemAPIClient: RedeemServerClient;
  ShopifyAPIClient: ShopifyClient;
  isHandlingProviderEvent: {
    status: boolean;
    address?: string;
  };
  leadingComponent?: any;
  loading: boolean;
  loaded: boolean;
  walletAvailable: boolean;
  walletAddress: string | undefined;
  wrongChain: boolean;
  startTime?: number;
  endTime?: number;
  now: number;
  offset: number;
  offsetLoaded?: boolean;
  countdown: string | unknown;
  connecting: boolean;
  tokenData: Token[];
  alreadyRedeemed: Token[];
  redeemsRemaining?: number;
  userInfoLoaded: boolean;
  questions: Question[];
  campaignActionType?: number;
  activeEligibleToken: Token | null;
  activeRedemption: RedemptionResult;
  campaignTitle: string;
  shopifyProducts: CampaignShopifyProductDetails[];
  shopifyQueue: any;
  appName: string;
  clientId: string;
  grantType: OAuthGrantType;
  storedShopifyCart: any;
  eligibleRewards: any[];
  confetti: any;
  isUnrestricted: boolean;
  isSoldOut: boolean;
  campaign?: Campaign;
  campaignProduct?: CampaignWithShopifyProduct;
  alreadyRedeemedByOthers: Token[];
  campaignCodeOptions: CodeOptionMap;
  // >>>>
  innerClaimText: string;
  redemptionSuccessText: string;
  eventName: string;
  eventDescription: string;
  emailSubjectLine: string | undefined;
  emailBody: string | undefined;
  eventBaseURL: string;
  eventConfirmation: string;
  ticketActionList: TicketActionString[];
  dataEventName: string;
  dataEventDescription: string;
  claimText: string;
  claimButtonImage: string;
  connectWalletText: string;
};

const store: Module<State, any> = {
  namespaced: true,
  state() {
    return {
      mmid: undefined,
      campaignId: undefined,
      productId: undefined,
      RedeemAPIClient: null as unknown as RedeemServerClient,
      ShopifyAPIClient: null as unknown as ShopifyClient,
      isHandlingProviderEvent: {
        status: false,
        address: undefined
      },
      leadingComponent: undefined,
      loading: false,
      loaded: false,
      walletAvailable: 'ethereum' in window ? true : false,
      walletAddress: undefined,
      wrongChain: false,
      campaign: undefined,
      campaignProduct: undefined,
      startTime: undefined,
      endTime: undefined,
      now: Date.now(),
      offset: 0,
      offsetLoaded: false,
      countdown: {},
      tokenData: [],
      alreadyRedeemed: [],
      alreadyRedeemedByOthers: [],
      redeemsRemaining: undefined,
      userInfoLoaded: false,
      questions: [],
      campaignActionType: undefined,
      campaignCodeOptions: {},
      activeEligibleToken: null,
      // This is set upon store initialization.
      activeRedemption: null as unknown as RedemptionResult,
      campaignTitle: '',
      shopifyProducts: [],
      shopifyQueue: undefined,
      appName: '',
      clientId: '',
      // `token` is deprecated, please use `signature` type
      grantType: OAuthGrantType.SIGNATURE,
      storedShopifyCart: undefined,
      eligibleRewards: [],
      confetti: undefined,
      isUnrestricted: false,
      isSoldOut: false,
      connecting: false,
      // <<<<< added for campaign >>>>
      innerClaimText: '',
      redemptionSuccessText: '',
      eventName: '',
      eventDescription: '',
      emailSubjectLine: '',
      emailBody: '',
      eventBaseURL: '',
      eventConfirmation: '',
      ticketActionList: [],
      dataEventName: '',
      dataEventDescription: '',
      claimText: '',
      claimButtonImage: '',
      connectWalletText: ''
    };
  },
  mutations: {
    setMMID(state, mmid) {
      state.mmid = mmid;
    },
    setRedeemAPIClient(state, RedeemAPIClient: RedeemServerClient) {
      state.RedeemAPIClient = RedeemAPIClient;
    },
    setShopifyAPIClient(state, ShopifyAPIClient: ShopifyClient) {
      state.ShopifyAPIClient = ShopifyAPIClient;
    },
    setIsUnrestricted(state, isUnrestricted: boolean) {
      state.isUnrestricted = isUnrestricted;
    },
    setStoredShopifyCart(state, storedShopifyCart: any) {
      state.storedShopifyCart = storedShopifyCart;
    },
    setConfetti(state, confetti: any) {
      state.confetti = confetti;
    },
    setEligibleRewards(state, eligibleRewards: any[]) {
      state.eligibleRewards = eligibleRewards;
    },
    setNow(state, now) {
      state.now = now;
    },
    setOffset(state, offset) {
      state.offset = offset;
      state.offsetLoaded = true;
    },
    updateCountdown(state, duration: any) {
      state.countdown = duration;
    },
    setWrongChain(state, bool: boolean) {
      state.wrongChain = bool;
    },
    setCampaign(state, campaign: Campaign) {
      state.campaign = campaign;
    },
    setCampaignProduct(state, campaignProduct: CampaignWithShopifyProduct) {
      state.campaignProduct = campaignProduct;
    },
    setCampaignStartTime(state, datetime: number) {
      state.startTime = datetime;
    },
    setCampaignEndTime(state, datetime: number) {
      state.endTime = datetime;
    },
    setCampaignIsSoldOut(state, isSoldOut: boolean) {
      state.isSoldOut = isSoldOut;
    },
    setIsHandlingProviderEvent(state, { status, address }: { status: boolean; address?: string }) {
      state.isHandlingProviderEvent = {
        status,
        address
      };
    },
    setCampaignId(state, campaignId: number) {
      state.campaignId = campaignId;
    },
    setProductId(state, productId: number) {
      state.productId = productId;
    },
    setCampaignTitle(state, campaignTitle: string) {
      state.campaignTitle = campaignTitle;
    },
    setCampaignShopifyProducts(state, shopifyProducts: CampaignShopifyProductDetails[]) {
      state.shopifyProducts = shopifyProducts;
    },
    setShopifyQueue(state, shopifyQueue: any) {
      state.shopifyQueue = shopifyQueue;
    },
    setTokensForCampaign(state, tokenData: Token[]) {
      state.tokenData = tokenData;
    },
    setAlreadyRedeemed(state, alreadyRedeemed: Token[]) {
      state.alreadyRedeemed = alreadyRedeemed;
    },
    setAlreadyRedeemedByOthers(state, alreadyRedeemedByOthers: Token[]) {
      state.alreadyRedeemedByOthers = alreadyRedeemedByOthers;
    },
    setQuestionsForCampaign(state, questions: Question[]) {
      state.questions = questions;
    },
    setActiveEligibleToken(state, token: Token) {
      state.activeEligibleToken = token;
    },
    setActiveRedemption(state, redemption: RedemptionResult) {
      state.activeRedemption = redemption;
    },
    setCampaignActionType(state, action) {
      state.campaignActionType = action;
    },
    setLeadingComponentForMMID(state, component) {
      state.leadingComponent = component;
    },
    setLoading(state, bool: boolean) {
      state.loading = bool;
    },
    setLoaded(state, bool: boolean) {
      state.loaded = bool;
    },
    setUserInfoLoaded(state, bool: boolean) {
      state.userInfoLoaded = bool;
    },
    setWalletAvailable(state, bool: boolean) {
      state.walletAvailable = bool;
    },
    setWalletAddress(state, address: string | undefined) {
      state.walletAddress = address;
    },
    setAppName(state, appName: string) {
      state.appName = appName;
    },
    setClientId(state, clientId: string) {
      state.clientId = clientId;
    },
    setGrantType(state, grantType: OAuthGrantType) {
      state.grantType = grantType;
    },
    setCampaignCodeOptions(state, options: CodeOptionMap) {
      state.campaignCodeOptions = options;
    },
    // >>>>>>>
    setInnerClaimText(state, innerClaimText: string) {
      state.innerClaimText = innerClaimText;
    },
    setRedemptionSuccessText(state, redemptionSuccessText: string) {
      state.redemptionSuccessText = redemptionSuccessText;
    },
    setEventName(state, eventName: string) {
      state.eventName = eventName;
    },
    setEventDescription(state, eventDescription: string) {
      state.eventDescription = eventDescription;
    },
    setEmailSubjectLine(state, emailSubjectLine: string) {
      state.emailSubjectLine = emailSubjectLine;
    },
    setEmailBody(state, emailBody: string) {
      state.emailBody = emailBody;
    },
    setEventBaseURL(state, eventBaseURL: string) {
      state.eventBaseURL = eventBaseURL;
    },
    setEventConfirmation(state, eventConfirmation: string) {
      state.eventConfirmation = eventConfirmation;
    },
    setTicketActionList(state, ticketActionList: TicketActionString[]) {
      state.ticketActionList = ticketActionList;
    },
    setDataEventName(state, dataEventName: string) {
      state.dataEventName = dataEventName;
    },
    setDataEventDescription(state, dataEventDescription: string) {
      state.dataEventDescription = dataEventDescription;
    },
    setClaimText(state, claimText: string) {
      state.claimText = claimText;
    },
    setClaimButtonImage(state, claimButtonImage: string) {
      state.claimButtonImage = claimButtonImage;
    },
    setConnectWalletText(state, connectWalletText: string) {
      state.connectWalletText = connectWalletText;
    }
  },
  actions: {
    async updateCountdown({ state, commit, getters }) {
      let now = Date.now();

      // Try to compute server offset
      if (!state.offsetLoaded) {
        try {
          const serverTimeResponse = await fetchWithTimeout(
            'https://oauth2.manifoldxyz.dev/time',
            {},
            1500
          );
          if (serverTimeResponse.status == 200) {
            const serverTime = (await serverTimeResponse.json()).time;
            now = Date.now();
            const offset = serverTime - now;
            commit('setOffset', offset);
          }
        } catch (e) {
          // Do nothing, not critical to get offset
          console.warn(`server time sync fetch error ${e}`);
          commit('setOffset', 0);
        }
      }

      commit('setNow', now);

      const countdownType = getters.countdownType;
      if (
        [CountdownType.NONE, CountdownType.LOADING, CountdownType.EXPIRED].includes(countdownType)
      ) {
        commit('updateCountdown', getCountdownFromTimestamp(0, true, false, state.offset));
      } else {
        let timestamp;
        if (countdownType == CountdownType.TO_START) {
          timestamp = state.startTime;
        } else if (countdownType == CountdownType.TO_END) {
          timestamp = state.endTime;
        }
        commit(
          'updateCountdown',
          getCountdownFromTimestamp(timestamp as number, true, false, state.offset) as string
        );
      }
    }
  },
  getters: {
    countdownType({ now, startTime, endTime, redeemsRemaining, offset }) {
      const actualNow = now + offset;
      if (!startTime) {
        return CountdownType.LOADING;
      } else if (redeemsRemaining === 0) {
        return CountdownType.EXPIRED;
      } else {
        if (actualNow < startTime) {
          return CountdownType.TO_START;
        } else if (!endTime) {
          return CountdownType.ACTIVE;
        } else if (actualNow < endTime) {
          return CountdownType.TO_END;
        } else if (endTime <= actualNow) {
          return CountdownType.EXPIRED;
        }
      }
      return CountdownType.NONE;
    },
    campaignIsActive(_, { countdownType }) {
      return [CountdownType.ACTIVE, CountdownType.TO_END].includes(countdownType);
    },
    countdown({ countdown }) {
      return countdown;
    },
    walletConnected({ walletAddress }) {
      return !!walletAddress;
    },
    productHasCampaign({ startTime }) {
      return Boolean(startTime);
    },
    campaignCodeOptionList({ campaignCodeOptions }) {
      return Object.values(campaignCodeOptions);
    }
  }
};

export default store;
